import { useQuery } from '@tanstack/react-query';
import { useFormikContext } from 'formik';
import { useEffect } from 'react';

import { queries } from '../../../common/api/queryKeys/queries';
import { useRole } from '../../../common/hooks/useRole';
import type { FormikCreateEnquiryValues } from '../CreateEnquiryFlow';
import { OrderMode } from '../types/OrderMode';

interface UseEnquiryOrderModeProps {
    isPlacingDirectOrder: boolean;
    isConfirmingDirectOrder: boolean;
}

export const useEnquiryOrderMode = ({ isPlacingDirectOrder, isConfirmingDirectOrder }: UseEnquiryOrderModeProps) => {
    const { values, isSubmitting, setValues, setFieldTouched, setFieldValue } =
        useFormikContext<FormikCreateEnquiryValues>();
    const isBusy = isSubmitting || isPlacingDirectOrder || isConfirmingDirectOrder;
    const selectedVesselQuery = useQuery({
        ...queries.vessels.detail(values.vesselId),
        enabled: !!values.vesselId && !isBusy,
    });
    const role = useRole();
    const selectedVessel = selectedVesselQuery.data;
    const customerSettingsQuery = useQuery({
        ...queries.customers.detail(values.customerId)._ctx.settings,
        enabled: !!values.customerId && !isBusy,
    });
    const canShowDirectOrder =
        role.isCustomer() &&
        role.hasWriteRights(values.customerId) &&
        !selectedVesselQuery.isError &&
        selectedVessel?.customerId === values.customerId &&
        selectedVessel?.directOrderAllowed === true &&
        !customerSettingsQuery.isError &&
        customerSettingsQuery.data?.approvalSettings?.activated === false;
    const isCheckingDirectOrderEligibility =
        !isBusy && (selectedVesselQuery.isFetching || customerSettingsQuery.isFetching);

    const isDirectOrder = values.orderMode === OrderMode.DIRECT_ORDER;

    useEffect(() => {
        if (!isBusy && !canShowDirectOrder && isDirectOrder) {
            setFieldValue('orderMode', OrderMode.ENQUIRY, false);
        }
    }, [isBusy, canShowDirectOrder, isDirectOrder, setFieldValue]);

    const handleOrderModeChange = (mode: OrderMode) => {
        if (isBusy || isCheckingDirectOrderEligibility) {
            return;
        }
        const isSelectingDirectOrder = mode === OrderMode.DIRECT_ORDER;
        setValues((currentValues) => {
            const clearSuppliers = isSelectingDirectOrder && currentValues.receiverSupplierIds.length !== 1;
            return {
                ...currentValues,
                orderMode: mode,
                spot: isSelectingDirectOrder ? false : currentValues.spot,
                receiverSupplierIds: clearSuppliers ? [] : currentValues.receiverSupplierIds,
                receiverContractIds: clearSuppliers ? [] : currentValues.receiverContractIds,
            };
        }, false);
        setFieldTouched('receiverSupplierIds', true, false);
        setFieldTouched('receiverContractIds', true, false);
    };

    return {
        selectedVessel,
        canShowDirectOrder,
        isCheckingDirectOrderEligibility,
        isDirectOrder,
        handleOrderModeChange,
    };
};
